import React from "react";
import _ from "lodash";
import ClaimBox from "./common/ClaimBox";
import CaseAgreement from "./common/CaseAgreement";
import AgreementDocCard from "./common/AgreementDocCard";
import useLifecycleStages from "../../../hooks/useLifecycleStages";

const PORTAL_NAME = "Cm";

const NegotiationState = ({
  currentState,
  negotiationRounds,
  isRespondent,
  ownerId,
  respondentId,
  caseDetails,
}) => {
  const { getStageConfig } = useLifecycleStages(PORTAL_NAME);

  if (!currentState?.key) return null;

  const stageConfig = getStageConfig(currentState.key, caseDetails);
  const roundKeys = _.keys(negotiationRounds)?.sort() || [];
  const lastRoundIndex = roundKeys.length - 1;

  /**
   * Decide whether respondent view should be shown
   */
  const checkShowRespondentView = (rounds) =>
    rounds?.respondentClaims?.some(
      (el) =>
        (el?.offer?.status === "offered" ||
          el?.offer?.statusAlais === "makeOffer") &&
        el?.offer?.offerorId === respondentId,
    );

  const RenderClaimBox = ({
    round,
    status,
    expand,
    errorText,
    showRespondantView,
  }) => (
    <ClaimBox
      title={`Round ${negotiationRounds[round].negotiationRound}`}
      key={round}
      expand={expand}
      isRespondent={isRespondent}
      claimItems={negotiationRounds[round]}
      enableCheckBox={currentState?.enableSelection}
      showRespondantView={
        showRespondantView || checkShowRespondentView(negotiationRounds[round])
      }
      status={status}
      errorText={errorText}
      ownerId={ownerId}
      negotiationRound={negotiationRounds[round].negotiationRound}
      respondentId={respondentId}
    />
  );

  const AgreementSection = () => (
    <>
      <div style={{ marginTop: 28 }}>
        <CaseAgreement
          title={`Final Award Summary for Case ID: ${caseDetails?.id}`}
          message={caseDetails?.negotiationSummary}
          summaryStatus={caseDetails?.summaryStatus === "completed"}
          isPaid={
            (currentState?.cta?.length ?? stageConfig.ctas?.length ?? 0) === 0
          }
        />
      </div>
      <div style={{ marginTop: 28 }}>
        <AgreementDocCard
          id={caseDetails?.id}
          status={caseDetails?.status}
          isRespondent={isRespondent}
          hasRating={caseDetails?.rating}
          hasAgreement={caseDetails?.hasAgreementDocument}
          type="Download"
        />
      </div>
    </>
  );

  // ---------- Derive round props purely from stageConfig ----------
  const getRoundProps = (idx) => {
    const isLast = idx === lastRoundIndex;
    const isErrorState = stageConfig.status === "error";
    const isSuccessState = stageConfig.status === "success";

    // Expand logic
    let expand = false;
    if (stageConfig.commentExpanded) {
      // If stage says expanded → expand only last round (or all if needed)
      expand = isLast;
    }

    // Status logic
    let status;
    if (isErrorState) {
      status = "error";
    } else if (isSuccessState) {
      status = isLast ? "success" : "error";
    } else if (!isLast) {
      // Previous rounds are marked error when not in error/success state
      status = "error";
    }

    // Error text – only show on last round when stage has errorText
    const errorText =
      isLast && stageConfig.errorText ? stageConfig.errorText : undefined;

    // Show respondent view when agreement is shown or stage indicates success
    const showRespondantView = !!stageConfig.showAgreement || isSuccessState;

    return { expand, status, errorText, showRespondantView };
  };

  return (
    <>
      <div style={{ marginTop: 28 }}>
        {roundKeys.map((round, idx) => {
          const { expand, status, errorText, showRespondantView } =
            getRoundProps(idx);

          return (
            <RenderClaimBox
              key={round}
              round={round}
              expand={expand}
              status={status}
              errorText={errorText}
              showRespondantView={showRespondantView}
            />
          );
        })}
      </div>

      {!!stageConfig.showAgreement && <AgreementSection />}
    </>
  );
};

export default React.memo(NegotiationState);
